import { Card } from "@/components/ui/card";
import { Activity, TrendingDown, Target, Zap, AlertCircle, Share2 } from "lucide-react";
import { CalculationResults } from "@/types/calculator";
import { Progress } from "@/components/ui/progress";
import FormulaComparison from "./FormulaComparison";
import ResultsCharts from "./ResultsCharts";
import TimeProjections from "./TimeProjections";
import ExportButtons from "./ExportButtons";
import SocialShare from "./SocialShare";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useState, useEffect } from "react";

interface ResultsPanelProps {
  results: CalculationResults | null;
  inputs?: any;
}

const ResultsPanel = ({ results, inputs }: ResultsPanelProps) => {
  const [shareUrl, setShareUrl] = useState<string>("");
  
  useEffect(() => {
    setShareUrl(window.location.origin);
  }, []);

  if (!results) {
    return (
      <Card className="p-6 md:p-8 border-card-border rounded-2xl shadow-soft">
        <div className="text-center">
          <div className="inline-block p-4 bg-secondary rounded-2xl mb-4">
            <Activity className="w-10 h-10 md:w-12 md:h-12 text-primary" />
          </div>
          <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">
            Ready to Calculate
          </h3>
          <p className="text-sm text-muted-foreground">
            Fill in your details and click "Calculate Results" to see your personalized walking plan.
          </p>
        </div>
      </Card>
    );
  }

  const { caloriesPerSession, weeklyCalories, estimatedWeightChange, adjustedTDEE, paceMinPerKm, stepsTarget, confidenceInterval } = results;

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Main Results Card */}
      <Card className="p-4 md:p-6 border-card-border rounded-2xl shadow-soft bg-gradient-to-br from-card to-secondary/30">
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <h2 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
            <Zap className="w-5 h-5 text-accent" />
            Your Results
          </h2>
          {inputs && <ExportButtons results={results} inputs={inputs} />}
        </div>

        <div className="grid grid-cols-2 gap-3 md:gap-4">
          <div className="bg-card p-3 md:p-4 rounded-xl border border-card-border">
            <p className="text-xs md:text-sm text-muted-foreground mb-1">Per Session</p>
            <p className="text-2xl md:text-3xl font-bold text-primary">
              {Math.round(caloriesPerSession)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">kcal</p>
          </div>

          <div className="bg-card p-3 md:p-4 rounded-xl border border-card-border">
            <p className="text-xs md:text-sm text-muted-foreground mb-1">Weekly Total</p>
            <p className="text-2xl md:text-3xl font-bold text-accent">
              {Math.round(weeklyCalories)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">kcal/week</p>
          </div>

          <div className="bg-card p-3 md:p-4 rounded-xl border border-card-border">
            <p className="text-xs md:text-sm text-muted-foreground mb-1">Weight Change</p>
            <p className="text-2xl md:text-3xl font-bold text-foreground">
              {estimatedWeightChange > 0 ? "-" : ""}{Math.abs(estimatedWeightChange).toFixed(2)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">kg/week</p>
          </div>

          <div className="bg-card p-3 md:p-4 rounded-xl border border-card-border">
            <p className="text-xs md:text-sm text-muted-foreground mb-1">Adjusted TDEE</p>
            <p className="text-2xl md:text-3xl font-bold text-foreground">
              {Math.round(adjustedTDEE)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">kcal/day</p>
          </div>
        </div>

        {/* Confidence Interval */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="mt-4 md:mt-6 p-3 md:p-4 bg-secondary/50 rounded-xl border border-card-border cursor-help">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs md:text-sm font-medium text-foreground flex items-center gap-2">
                    Confidence Range
                    <AlertCircle className="w-3 h-3 md:w-4 md:h-4 text-muted-foreground" />
                  </p>
                  <span className="text-xs text-muted-foreground">
                    ±{(((confidenceInterval.upper - confidenceInterval.lower) / (2 * caloriesPerSession)) * 100).toFixed(0)}%
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs md:text-sm text-muted-foreground">
                    {Math.round(confidenceInterval.lower)} - {Math.round(confidenceInterval.upper)} kcal
                  </span>
                </div>
                <Progress 
                  value={75} 
                  className="h-1.5 md:h-2" 
                />
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="text-xs">{confidenceInterval.reason}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </Card>

      {/* Time Projections */}
      <TimeProjections projections={results.timeProjections} />

      {/* Formula Comparison */}
      <FormulaComparison formulaResults={results.formulaResults} />

      {/* Charts */}
      <ResultsCharts weeklyPlan={results.weeklyPlan} />

      {/* Pace & Steps Card */}
      <Card className="p-4 md:p-6 border-card-border rounded-2xl shadow-soft">
        <h3 className="text-base md:text-lg font-semibold text-foreground mb-3 md:mb-4 flex items-center gap-2">
          <Target className="w-5 h-5 text-primary" />
          Targets
        </h3>

        <div className="space-y-3 md:space-y-4">
          <div className="flex justify-between items-center p-3 bg-secondary rounded-xl">
            <span className="text-sm font-medium text-foreground">Pace</span>
            <span className="text-base md:text-lg font-bold text-primary">
              {paceMinPerKm.toFixed(1)} min/km
            </span>
          </div>

          <div className="flex justify-between items-center p-3 bg-secondary rounded-xl">
            <span className="text-sm font-medium text-foreground">Daily Steps Target</span>
            <span className="text-base md:text-lg font-bold text-accent">
              {stepsTarget.toLocaleString()}
            </span>
          </div>
        </div>
      </Card>

      {/* Weekly Plan Preview */}
      <Card className="p-4 md:p-6 border-card-border rounded-2xl shadow-soft">
        <h3 className="text-base md:text-lg font-semibold text-foreground mb-3 md:mb-4 flex items-center gap-2">
          <TrendingDown className="w-5 h-5 text-accent" />
          12-Week Plan Preview
        </h3>

        <div className="space-y-2 md:space-y-3">
          {results.weeklyPlan.slice(0, 4).map((week) => (
            <div key={week.week} className="p-3 md:p-4 bg-secondary rounded-xl border border-card-border">
              <div className="flex justify-between items-center mb-2">
                <span className="font-semibold text-sm md:text-base text-foreground">Week {week.week}</span>
                <span className="text-xs md:text-sm text-accent font-medium">
                  -{week.cumulativeWeightChange.toFixed(2)} kg total
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs md:text-sm text-muted-foreground">
                <span>{week.sessions} sessions</span>
                <span>{week.totalMinutes} min total</span>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Social Sharing */}
      <Card className="p-4 md:p-6 border-card-border rounded-2xl shadow-soft bg-gradient-to-br from-accent/5 to-primary/5">
        <h3 className="text-base md:text-lg font-semibold text-foreground mb-3 md:mb-4 flex items-center gap-2">
          <Share2 className="w-5 h-5 text-primary" />
          Share Your Progress
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Share your personalized walking plan with friends or on social media to stay motivated!
        </p>
        <SocialShare results={results} shareUrl={shareUrl} />
      </Card>
    </div>
  );
};

export default ResultsPanel;
